import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { RefreshCw, Receipt, Truck, ShoppingBag, Clock, Search, X, XCircle, Navigation, Printer, Download, TestTube, Trash2, Filter, Bell, CheckCircle, CheckCircle2, Archive, ArrowRight, Volume2, VolumeX, User, Phone, Mail, MapPin, CreditCard, Beaker, Tag, MessageSquare, AlarmClock, Flame, Bike, BadgeCheck, Info } from 'lucide-react';
import { spacing, borderRadius, fontSize, fontWeight, shadows } from '../../styles/theme';
import RedBanner from '../components/RedBanner';
import { generatePrintHTML } from '../utils/printTemplate';
import NotificationPopup from '../components/NotificationPopup';
import NotificationModal from '../components/NotificationModal';
import Widget from '../components/Widget';
import WebvonkLoader from '../components/WebvonkLoader';
import { getKmfcClient } from '../../lib/supabase';
import { usePermissions } from '../hooks/usePermissions';
import { useSession } from '../../contexts/SessionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import SoundService from '../services/SoundService';
import DataSyncService from '../services/DataSyncService';
import StatusPopup from '../components/StatusPopup';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const heartbeat = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); }
  70% { box-shadow: 0 0 0 4px rgba(220, 38, 38, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
`;

const Container = styled.div`
  height: 100vh;
  background-color: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const ConnectionBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px 20px;
  background: ${props => props.$offline ? 'rgba(239,68,68,0.12)' : 'rgba(249,115,22,0.10)'};
  border-bottom: 1px solid ${props => props.$offline ? 'rgba(239,68,68,0.25)' : 'rgba(249,115,22,0.25)'};
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$offline ? '#ef4444' : '#f97316'};
  cursor: pointer;
  user-select: none;
  &:hover { background: ${props => props.$offline ? 'rgba(239,68,68,0.18)' : 'rgba(249,115,22,0.16)'}; }
`;

const BannerDot = styled.div`
  width: 7px; height: 7px; border-radius: 50%;
  background: ${props => props.$offline ? '#ef4444' : '#f97316'};
  flex-shrink: 0;
`;

const InfoBadge = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 6px;
  color: ${p => p.$color || '#6b7280'};
  border: 1.5px solid ${p => p.$color ? p.$color + '66' : '#6b728044'};
  background: ${p => p.$color ? p.$color + '14' : 'transparent'};
  cursor: default;
  user-select: none;

  &:hover > span {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
`;

const InfoTooltip = styled.span`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(-4px);
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 400;
  color: ${p => p.theme.colors.text};
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 100;
`;

const PageHeader = styled.div`
  padding: ${spacing.lg} 0 ${spacing.xl};

  h1 {
    font-size: clamp(24px, 3vw, 32px);
    font-weight: ${fontWeight.bold};
    color: ${props => props.theme.colors.text};
    letter-spacing: -0.02em;
    line-height: 1;
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: ${spacing.md} 0;
  }
`;

const WelcomeSubtitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.theme.colors.muted};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
  opacity: ${p => p.$visible ? 1 : 0};
  max-height: ${p => p.$visible ? '24px' : '0px'};
  margin-bottom: ${p => p.$visible ? '6px' : '0px'};
  transition:
    opacity 1s ease,
    max-height 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.5s,
    margin-bottom 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.5s;
`;

const Content = styled.div`
  padding: ${spacing.xl};
  height: calc(100vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    padding: ${spacing.md};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${spacing.lg};
  margin-bottom: ${spacing.xl};
  width: 100%;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing.xs};
  letter-spacing: -0.02em;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.8;
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: ${spacing.xl};
  text-align: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &:hover {
    border-color: ${props => props.theme.colors.border};
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    background: ${props => props.theme.colors.background};
    &::before { opacity: 1; }
    ${StatValue} { color: ${props => props.theme.colors.brand}; }
  }
`;

const NavBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: ${spacing.lg};
  overflow-x: auto;
  padding: 2px 0 6px;
  width: 100%;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const NavTab = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 50px;
  border: 1px solid ${props => props.active
    ? props.theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'
    : props.theme.colors.border};
  background: ${props => props.active
    ? props.theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)'
    : props.theme.colors.surface};
  color: ${props => props.active ? props.theme.colors.text : props.theme.colors.muted};
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: ${props => props.active ? '0 1px 6px rgba(0,0,0,0.1)' : 'none'};
  transition: all 0.18s ease;
  &:hover { color: ${props => props.theme.colors.text}; }
  &:active { transform: scale(0.96); }
`;

const TabCounter = styled.span`
  font-weight: 700;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 20px;
  background: ${props => props.active
    ? 'rgba(249,115,22,0.18)'
    : 'rgba(128,128,128,0.12)'};
  color: ${props => props.active ? '#f97316' : 'inherit'};
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 12px;
  padding-bottom: 100px;
  width: 100%;

  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 10px; }
`;

const STATUS_ACCENT = {
  nieuw: '#FF9500',
  bereiding: '#FF3B30',
  klaar: '#34C759',
  onderweg: '#007AFF',
  bezorgd: '#6b7280',
  voltooid: '#6b7280',
  unpaid: '#FF3B30',
  payment_failed: '#FF3B30',
  payment_pending: '#FF9500',
  payment_cancelled: '#FF3B30',
};
const getAccent = (status) => STATUS_ACCENT[status] || '#6b7280';

const OrderBlock = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 18px;
  border: 1px solid ${props => props.theme.colors.border};
  transition: background 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 8px rgba(0,0,0,0.05);
  animation: ${props => props.isUnpaid ? css`${heartbeat} 2s infinite` : 'none'};
  position: relative;
  cursor: pointer;
  padding: 14px 16px;
  gap: 9px;

  &:hover {
    background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'};
    box-shadow: 0 3px 16px rgba(0,0,0,0.08);
  }

  &:active { transform: scale(0.985); transition: all 0.08s ease; }
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

const OrderNumber = styled.div`
  font-size: 11px;
  font-weight: ${fontWeight.medium};
  color: ${props => props.theme.colors.muted};
  opacity: 0.5;
  letter-spacing: 0.3px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex: 1;
  min-width: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-shrink: 0;
`;

const CardRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const TimeAndIcon = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-size: 14px;
  color: ${props => props.theme.colors.muted};
  font-weight: ${fontWeight.medium};
  flex: 1;
  min-width: 0;
`;

const DeliveryIcon = styled.div`
  color: ${props => props.theme.colors.text};
  opacity: 0.7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ItemsBadge = styled.div`
  background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  color: ${props => props.theme.colors.muted};
  border: 1px solid ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
  border-radius: 50px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: ${fontWeight.semibold};
  flex-shrink: 0;
`;

const UnpaidBadge = styled.span`
  background: rgba(239,68,68,0.1);
  color: #ef4444;
  border: 1px solid rgba(239,68,68,0.3);
  border-radius: 50px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: ${fontWeight.bold};
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background: ${props => props.accent ? `${props.accent}22` : 'rgba(255,255,255,0.1)'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.accent || props.theme.colors.brand};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover { background: ${props => props.accent ? `${props.accent}44` : 'rgba(255,255,255,0.18)'}; transform: scale(1.1); }
  &:active { transform: scale(0.95); }
`;

const DeleteButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background: rgba(220,38,38,0.15);
  color: #dc2626;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover { background: rgba(220,38,38,0.3); transform: scale(1.1); }
`;

const TimerBadge = styled.div`
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.06)' : props.theme.colors.border};
  border: 1px solid ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.1)' : props.theme.colors.border};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${borderRadius.sm};
  font-size: ${fontSize.sm};
  color: ${props => props.isLate ? props.theme.colors.error : props.theme.colors.success};
  font-weight: ${fontWeight.bold};
`;

const CustomerName = styled.div`
  font-size: 15px;
  color: ${props => props.theme.colors.text};
  font-weight: ${fontWeight.semibold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BlockBody = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${fontSize.sm};
`;

const InfoLabel = styled.span`color: #666;`;
const InfoValue = styled.span`color: #fff; font-weight: ${fontWeight.semibold};`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: ${fontWeight.bold};
  text-transform: uppercase;
  color: white;
  background: ${props => {
    switch(props.status) {
      case 'unpaid': return '#ef4444';
      case 'nieuw': return '#3b82f6';
      case 'bereiding': return '#f59e0b';
      case 'klaar': return '#10b981';
      case 'onderweg': return '#8b5cf6';
      case 'bezorgd': return '#22c55e';
      case 'voltooid': return '#22c55e';
      case 'geannuleerd': return '#6b7280';
      default: return '#6b7280';
    }
  }};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const BlockFooter = styled.div`
  display: flex;
  gap: ${spacing.sm};
  padding-top: ${spacing.sm};
  border-top: 1px solid ${props => props.theme.colors.border};
  margin-top: 2px;
`;

const BlockButton = styled.button`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  padding: 0;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  color: ${props => props.theme.colors.text};
  border: none;
  border-radius: 50%;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)'}; }
  &:active { transform: scale(0.94); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const PaidButton = styled(BlockButton)`
  background: rgba(21,128,61,0.15);
  color: #16a34a;
  border-color: rgba(21,128,61,0.3);
  margin-top: 4px;
  flex: none;
  width: 100%;
  &:hover { background: rgba(21,128,61,0.25); }
`;

const SecondaryBlockButton = styled(BlockButton)`
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.muted};
  border-color: ${props => props.theme.colors.border};
  &:hover { background: ${props => props.theme.colors.background}; color: ${props => props.theme.colors.text}; }
`;

const RouteButton = styled(BlockButton)`
  background: rgba(0,122,255,0.12);
  color: #007AFF;
  font-size: 12px;
  font-weight: 600;
  width: auto;
  padding: 0 14px;
  border-radius: 100px;
  gap: 6px;
  &:hover { background: rgba(0,122,255,0.2); }
`;

const emptyBounce = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 32px;
  gap: 16px;
  grid-column: 1 / -1;
`;

const EmptyIcon = styled.div`
  color: ${props => props.theme.colors.text};
  opacity: 0.25;
  animation: ${emptyBounce} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.theme.colors.muted};
  opacity: 0.5;
  text-align: center;
  animation: ${emptyBounce} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.08s both;
`;

const EmptySub = styled.div`display: none;`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  padding: ${spacing.md} ${spacing.lg};
  margin-bottom: ${spacing.lg};
  width: 100%;
  max-width: 600px;
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
  flex: 1;
  font-size: ${fontSize.sm};
  &::placeholder { color: ${props => props.theme.colors.muted}; }
  &:focus { outline: none; }
`;

const ArchiveSection = styled.div`margin-bottom: ${spacing.md};`;

const ArchiveDateHeader = styled.div`
  font-size: ${fontSize.sm};
  color: #666;
  font-weight: ${fontWeight.semibold};
  margin-bottom: ${spacing.md};
  padding-bottom: ${spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

// ─── Archive UI ───────────────────────────────────────────────────────────────

const ArchiveWrap = styled.div`
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 40px;
`;

const ArchivePeriodCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 18px;
  overflow: hidden;
`;

const ArchiveDateRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
  height: 52px;
  gap: 12px;
  & + & { border-top: 1px solid ${p => p.theme.colors.border}; }
`;

const ArchiveDateLabel = styled.label`
  font-size: 15px;
  color: ${p => p.theme.colors.text};
  width: 110px;
  flex-shrink: 0;
`;

const ArchiveDateInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-size: 15px;
  font-family: inherit;
  color: ${p => p.theme.colors.text};
  text-align: right;
  cursor: pointer;
  color-scheme: ${p => p.theme.mode};
`;

const ArchiveInfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 14px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  border-radius: 14px;
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.5;
`;

const ArchiveExportBtn = styled.button`
  width: 100%;
  padding: 15px;
  background: ${p => p.theme.mode === 'dark' ? '#ffffff' : '#141414'};
  color: ${p => p.theme.mode === 'dark' ? '#000000' : '#ffffff'};
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
  &:active { opacity: 0.7; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ArchiveSecondaryBtn = styled.button`
  width: 100%;
  padding: 15px;
  background: ${p => p.theme.colors.surface};
  color: ${p => p.theme.colors.text};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  transition: background 0.15s ease, opacity 0.15s ease;
  &:hover { background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ArchiveDivider = styled.div`
  height: 1px;
  background: ${p => p.theme.colors.border};
  margin: 8px 0;
`;

const PriceFilterContainer = styled.div`position: relative;`;

const PriceFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: ${props => props.theme.colors.background}; }
`;

const PriceFilterDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  padding: ${spacing.md};
  margin-top: ${spacing.xs};
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const PriceFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.sm};
  &:last-child { margin-bottom: 0; }
`;

const PriceInput = styled.input`
  flex: 1;
  padding: ${spacing.xs};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.sm};
  font-size: 13px;
  &:focus { outline: none; border-color: ${props => props.theme.colors.brand}; }
`;

const PriceLabel = styled.span`
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
  min-width: 20px;
`;

const ZSMClock = styled(Clock)`
  color: ${props => props.isZSM ? '#f97316' : '#ffffff'};
`;

const ZSMTime = styled.span`
  color: ${props => props.isZSM ? '#f97316' : '#ffffff'};
`;

const ZSMBadge = styled.span`
  color: #f97316;
  font-size: 12px;
  font-weight: 700;
  margin-left: 2px;
  letter-spacing: 0.2px;
  text-transform: uppercase;
`;

const TestBadge = styled.span`
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-left: 8px;
`;

const TestCounter = styled.span`
  background: #ff4444;
  color: white;
  padding: 2px 6px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: bold;
  margin-left: 4px;
  min-width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const WarningBanner = styled.div`
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: ${spacing.xl};
  margin-top: ${spacing.xl};
  margin-bottom: ${spacing.xl};
  box-shadow: 0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1);
`;

const WarningText = styled.div`
  color: #ffffff;
  font-size: 18px;
  font-family: 'Futura', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 600;
  line-height: 1.6;
  text-align: center;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
`;

// Modal styles
const Modal = styled.div`
  display: flex;
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'all' : 'none'};
  animation: ${props => props.visible ? overlayIn : 'none'} 0.22s ease forwards;
`;

const TestOrderModal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const TestOrderModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl};
  max-width: 400px;
  width: 90%;
  border: 1px solid ${props => props.theme.colors.border};
`;

const TestOrderModalTitle = styled.h2`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing.md};
  text-align: center;
`;

const TestOrderModalDescription = styled.p`
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.muted};
  margin-bottom: ${spacing.lg};
  text-align: center;
  line-height: 1.5;
`;

const TestOrderOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
`;

const TestOrderOption = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 2px solid ${props => props.selected ? props.theme.colors.brand : props.theme.colors.border};
  background: ${props => props.selected ? props.theme.colors.brand + '20' : props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${fontSize.md};
  font-weight: ${props => props.selected ? fontWeight.semibold : fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { border-color: ${props => props.theme.colors.brand}; background: ${props => props.theme.colors.brand + '10'}; }
`;

const TestOrderOptionIcon = styled.div`
  display: flex; align-items: center; justify-content: center;
  width: 40px; height: 40px;
  border-radius: ${borderRadius.md};
  background: ${props => props.theme.colors.brand + '20'};
  color: ${props => props.theme.colors.brand};
`;

const TestOrderOptionText = styled.div`display: flex; flex-direction: column; align-items: flex-start;`;
const TestOrderOptionTitle = styled.div`font-weight: ${fontWeight.semibold}; margin-bottom: 2px;`;
const TestOrderOptionSubtitle = styled.div`font-size: ${fontSize.sm}; color: ${props => props.theme.colors.muted};`;

const TestOrderModalButtons = styled.div`display: flex; gap: ${spacing.md};`;

const TestOrderModalButton = styled.button`
  flex: 1;
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  ${props => props.primary && `background: ${props.theme.colors.brand}; color: white; border: none; &:hover { background: ${props.theme.colors.brand}dd; }`}
  ${props => !props.primary && `background: ${props.theme.colors.background}; color: ${props.theme.colors.text}; border: 1px solid ${props.theme.colors.border}; &:hover { background: ${props.theme.colors.border}; }`}
`;

const modalOpen = keyframes`
  from { opacity: 0; transform: scale(0.94) translateY(20px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

const overlayIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.border};
  width: 95vw;
  max-width: 1140px;
  max-height: 92vh;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 32px 80px rgba(0,0,0,0.5);
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  animation: ${modalOpen} 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  @media (max-width: 800px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

const DetailCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 16px;
  color: ${props => props.theme.colors.text};
`;

const DetailSectionLabel = styled.div`
  font-size: 11px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const DetailInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  &:last-child { border-bottom: none; padding-bottom: 0; }
  &:first-child { padding-top: 0; }
`;

const DetailRowIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'};
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.muted};
  flex-shrink: 0;
`;

const DetailRowContent = styled.div`flex: 1; min-width: 0;`;
const DetailRowLabel = styled.div`font-size: 11px; color: ${props => props.theme.colors.muted}; margin-bottom: 2px;`;
const DetailRowValue = styled.div`font-size: 14px; font-weight: ${fontWeight.medium}; color: ${props => props.theme.colors.text}; word-break: break-word;`;

const DetailTimeDisplay = styled.div`
  font-size: 22px;
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  line-height: 1;
  letter-spacing: -1px;
  font-variant-numeric: tabular-nums;
`;

const DetailMoneyRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  font-size: ${props => props.total ? '15px' : '14px'};
  font-weight: ${props => props.total ? fontWeight.bold : fontWeight.regular};
  color: ${props => props.total ? props.theme.colors.text : props.theme.colors.muted};
  border-top: ${props => props.total ? `1px solid ${props.theme.colors.border}` : 'none'};
  margin-top: ${props => props.total ? '4px' : '0'};
  padding-top: ${props => props.total ? '10px' : '5px'};
`;

const DetailActionButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${props => props.accent || props.theme.colors.brand};
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 12px ${props => props.accent ? props.accent + '44' : 'rgba(0,0,0,0.2)'};
  &:hover { filter: brightness(1.1); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

const DetailDangerButton = styled(DetailActionButton)`
  background: #FF3B30;
  box-shadow: 0 4px 12px rgba(255,59,48,0.3);
`;

const ItemRow = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.03)'};
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: 6px;
  &:last-child { margin-bottom: 0; }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)'};
  border-bottom: 1px solid ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.07)'};
  border-radius: 20px 20px 0 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(20px);
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  &:hover {
    background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)'};
    transform: translateY(-1px);
    box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  }
  &:active { transform: scale(0.97); }
  svg { width: 18px; height: 18px; }
`;

const ModalBody = styled.div`padding: 20px; display: flex; flex-direction: column; gap: 12px;`;

const Section = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.2)'};
  border-radius: 12px;
  border-left: 3px solid ${props => props.theme.colors.brand};
  border: 1px solid ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'transparent'};
`;

const ErrorModal = styled.div`
  position: fixed;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  background: ${props => props.theme.colors.surface};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl};
  max-width: 400px;
  width: 90%;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${shadows.lg};
  z-index: 10001;
`;

const VandaagSectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  opacity: 0.5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0 2px;
  margin-bottom: 10px;
  margin-top: 24px;
`;

const VandaagCount = styled.span`
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)'};
  color: inherit;
  border-radius: 20px;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 700;
`;

function VandaagVoltooidSection({ allOrders, onOrderClick, formatOrderCode, getDeliveryInfo }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const completedStatuses = ['bezorgd', 'voltooid', 'geannuleerd'];
  const vandaag = allOrders.filter(o => {
    if (!completedStatuses.includes(o.status)) return false;
    const d = new Date(o.placed_at || o.created_at);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  });

  return (
    <div>
      <VandaagSectionLabel>
        Vandaag voltooid
        <VandaagCount>{vandaag.length}</VandaagCount>
      </VandaagSectionLabel>
      {vandaag.length === 0 ? (
        <div style={{ textAlign: 'center', opacity: 0.35, fontSize: 14, padding: '18px 0 8px' }}>
          Nog geen voltooide bestellingen vandaag
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {vandaag.map(order => (
            <OrderBlock key={order.id} status={order.status} onClick={() => onOrderClick(order)}>
              <BlockHeader>
                <HeaderLeft>
                  <CustomerName>{order.customer_name || 'Gast'}</CustomerName>
                </HeaderLeft>
                <HeaderRight>
                  <OrderNumber>{formatOrderCode(order.order_code)}</OrderNumber>
                </HeaderRight>
              </BlockHeader>
              <CardRow>
                <DeliveryIcon>
                  {order.delivery_type === 'delivery' ? <Truck size={18} /> : <ShoppingBag size={18} />}
                </DeliveryIcon>
                <TimeAndIcon>
                  {order.delivery_type === 'delivery' && order.delivery_city
                    ? <span style={{ marginRight: 4 }}>{order.delivery_city}</span>
                    : null}
                  <span>{getDeliveryInfo(order)}</span>
                </TimeAndIcon>
                <ItemsBadge>{order.items?.length ?? 0} items</ItemsBadge>
              </CardRow>
            </OrderBlock>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrdersScreen() {
  const { colors, themeMode } = useTheme();
  const isLight = themeMode === 'light';
  const navigate = useNavigate();
  const soundService = useRef(SoundService);

  const [showWelcome, setShowWelcome] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem('kmfc-sound-enabled') || 'false'); } catch { return false; }
  });
  const [soundError, setSoundError] = useState(false);
  const [showSoundLabel] = useState(false);
  const [hoverSoundIcon, setHoverSoundIcon] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowWelcome(false), 3500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    SoundService.onError(() => setSoundError(true));
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'kmfc-sound-enabled') {
        try { setSoundEnabled(JSON.parse(e.newValue || 'false')); } catch {}
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const [stats, setStats] = useState({ readyForDelivery: 0, todayOrdersCount: 0 });
  const [tabCounts, setTabCounts] = useState({ new: 0, preparing: 0, ready: 0, out_for_delivery: 0, completed: 0, archive: 0 });
  const [activeTab, setActiveTab] = useState('new');
  const [allOrders, setAllOrders] = useState([]); // Volledige lijst
  const [orders, setOrders] = useState([]); // Gefilterd voor huidige tab
  const [loading, setLoading] = useState(true);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const hasLoadedDataRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [priceFilter, setPriceFilter] = useState({ min: '', max: '' });
  const [priceFilterDropdown, setPriceFilterDropdown] = useState(false);
  const [timer, setTimer] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [simulateConnectionState, setSimulateConnectionState] = useState(null); // null | 'standby' | 'offline'
  const [connectionErrorCode, setConnectionErrorCode] = useState(null);
  const wasOfflineRef = useRef(false);
  const [status, setStatus] = useState(null);
  const [currentShopStatus, setCurrentShopStatus] = useState(null);
  const [archiveLoadedDays, setArchiveLoadedDays] = useState(3);
  const [archiveSearchResults, setArchiveSearchResults] = useState(null);
  const [archiveSearchLoading, setArchiveSearchLoading] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const thirtyDaysAgoStr = new Date(Date.now() - 86400000 * 30).toISOString().split('T')[0];
  const [archiveStartDate, setArchiveStartDate] = useState(thirtyDaysAgoStr);
  const [archiveEndDate, setArchiveEndDate] = useState(todayStr);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [showClosedWarning, setShowClosedWarning] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);

  const { permissions } = usePermissions();
  const { isAuthenticated, isReady } = useSession();
  const isAdmin = permissions?.slug === 'admin';
  const roleId = parseInt(permissions?.id, 10);
  const canDelete = roleId === 1 || roleId === 2 || permissions?.slug === 'admin' || permissions?.slug === 'manager';
  const canSeeTestToggle = roleId === 1 || roleId === 2 || permissions?.slug === 'admin' || permissions?.slug === 'manager';

  const [isCreatingTestOrder, setIsCreatingTestOrder] = useState(false);
  const [showTestOrderModal, setShowTestOrderModal] = useState(false);
  const [testOrderType, setTestOrderType] = useState('pickup');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showDeleteUnpaidConfirmModal, setShowDeleteUnpaidConfirmModal] = useState(false);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [copiedPaymentId, setCopiedPaymentId] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [orderToMarkAsPaid, setOrderToMarkAsPaid] = useState(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [markAsPaidConfirmed, setMarkAsPaidConfirmed] = useState(false);

  // DataSyncService initialiseren
  useEffect(() => {
    if (!isReady || !isAuthenticated) return;

    const init = async () => {
      // Start DataSyncService
      const unsubError = DataSyncService.onError((code) => setConnectionErrorCode(code));

      const unsubLoading = DataSyncService.onLoadingChange((isLoading) => {
        setLoading(isLoading);
      });

      const unsubOrders = DataSyncService.subscribe('orders', (ordersData) => {
        if (!ordersData) return;
        setAllOrders(ordersData.all || []);
        setLastUpdateTime(new Date());
        setConnectionErrorCode(null);
      });

      const unsubStats = DataSyncService.subscribe('stats', (statsData) => {
        if (!statsData) return;
        setStats({
          readyForDelivery: statsData.readyForDelivery,
          todayOrdersCount: statsData.todayOrdersCount,
        });
        setTabCounts(statsData.tabCounts);
        if (!hasLoadedDataRef.current) {
          const tc = statsData.tabCounts;
          const tabOrder = [
            { key: 'new',              count: tc.new },
            { key: 'bereiding',        count: tc.preparing },
            { key: 'klaar',            count: tc.ready },
            { key: 'onderweg',         count: tc.out_for_delivery },
            { key: 'completed',        count: tc.completed },
          ];
          const first = tabOrder.find(t => t.count > 0);
          setActiveTab(first ? first.key : 'new');
          hasLoadedDataRef.current = true;
        }
        setHasLoadedData(true);
      });

      await DataSyncService.start();
      await SoundService.startDataSync?.();

      // Unlock AudioContext bij eerste user gesture op het scherm
      const unlock = () => { SoundService.unlockOnGesture?.(); document.removeEventListener('touchstart', unlock); document.removeEventListener('mousedown', unlock); };
      document.addEventListener('touchstart', unlock, { once: true, passive: true });
      document.addEventListener('mousedown', unlock, { once: true });

      return () => {
        unsubError();
        unsubLoading();
        unsubOrders();
        unsubStats();
        DataSyncService.stop();
      };
    };

    let cleanup;
    init().then(fn => { cleanup = fn; });
    return () => { if (cleanup) cleanup(); };
  }, [isReady, isAuthenticated]);

  // Trigger refresh wanneer tab wisselt
  useEffect(() => {
    console.log(`📂 Tab changed to: ${activeTab} - triggering refresh`);
    setLastUpdateTime(new Date());
    setSearchQuery('');
    setArchiveSearchResults(null);
    DataSyncService.forceSync();
  }, [activeTab]);

  // Trigger refresh bij window focus
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 Window focused - triggering refresh');
      setLastUpdateTime(new Date()); // Direct update van tijd
      DataSyncService.forceSync();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Trigger refresh bij page visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👁️ Page became visible - triggering refresh');
        setLastUpdateTime(new Date()); // Direct update van tijd
        DataSyncService.forceSync();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);


  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle arrow keys when not typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }

      const tabs = ['new', 'bereiding', 'klaar', 'onderweg', 'completed', 'archive'];
      const currentIndex = tabs.indexOf(activeTab);

      // Arrow key navigation removed

      // Enter = Open order
      if (e.key === 'Enter') {
        e.preventDefault();
        
        // Open de eerste order in de lijst
        if (orders.length > 0) {
          console.log(`⌨️ Open eerste order: ${orders[0].id}`);
          setSelectedOrder(orders[0]);
          setSelectedStatus(orders[0].status);
        }
        return;
      }

      // Arrow navigation
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        e.preventDefault();
        const newTab = tabs[currentIndex - 1];
        setActiveTab(newTab);
        console.log(`⌨️ Navigeer naar tab: ${newTab}`);
      } else if (e.key === 'ArrowRight' && currentIndex < tabs.length - 1) {
        e.preventDefault();
        const newTab = tabs[currentIndex + 1];
        setActiveTab(newTab);
        console.log(`⌨️ Navigeer naar tab: ${newTab}`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, orders]);

  // Filter orders wanneer allOrders of activeTab verandert
  useEffect(() => {
    setOrders(filterOrdersForTab(allOrders, activeTab));
  }, [allOrders, activeTab]);



  // Closed warning — uit DataSyncService, geen extra Supabase calls
  useEffect(() => {
    if (!isAuthenticated || !isReady) return;
    import('../services/DataSyncService').then(({ default: dataSyncService }) => {
      return dataSyncService.subscribe('shopStatus', (status) => {
        setCurrentShopStatus(status);
        if (status && !status.isOpen && status.source === 'manual_close') setShowClosedWarning(true);
        else setShowClosedWarning(false);
      });
    });
  }, [isAuthenticated, isReady]);

  // Offline detectie — geluid als poll > 10 minuten oud
  useEffect(() => {
    const interval = setInterval(() => {
      const isOffline = (Date.now() - lastUpdateTime.getTime()) > 10 * 60 * 1000;
      if (isOffline && !wasOfflineRef.current) {
        wasOfflineRef.current = true;
        soundService.current.playOnce();
      } else if (!isOffline) {
        wasOfflineRef.current = false;
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [lastUpdateTime]);

  // Keyboard navigatie
  useEffect(() => {
    const tabs = ['new', 'bereiding', 'klaar', 'onderweg', 'completed', 'archive'];
    const handle = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      const i = tabs.indexOf(activeTab);
      if (e.key === 'ArrowLeft' && i > 0) { e.preventDefault(); setActiveTab(tabs[i - 1]); }
      if (e.key === 'ArrowRight' && i < tabs.length - 1) { e.preventDefault(); setActiveTab(tabs[i + 1]); }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [activeTab]);

  // Timer countdown — refresh every 30 seconds (minutes resolution, like Swift)
  useEffect(() => {
    const compute = () => {
      const t = {};
      orders.forEach(order => {
        let deliveryTime = order.delivery_time;
        if (!deliveryTime && order.is_asap && order.placed_at) {
          deliveryTime = new Date(new Date(order.placed_at).getTime() + 60 * 60 * 1000).toISOString();
        }
        if (deliveryTime && !['completed', 'archive', 'voltooid', 'bezorgd', 'geannuleerd'].includes(order.status)) {
          t[order.id] = getCountdownTime(deliveryTime);
        }
      });
      setTimer(t);
    };
    compute();
    const interval = setInterval(compute, 30000);
    return () => clearInterval(interval);
  }, [orders, activeTab]);

  const filterOrdersForTab = (orderList, tab) => {
    const today = new Date().toISOString().split('T')[0];
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

    switch (tab) {
      case 'new':
        return orderList.filter(o =>
          ['nieuw', 'nieuw'].includes(o.status) ||
          (['unpaid', 'payment_failed', 'payment_pending', 'payment_cancelled'].includes(o.status) && o.placed_at && new Date(o.placed_at) < fiveMinutesAgo)
        );
      case 'bereiding':
        return orderList.filter(o => o.status === 'bereiding');
      case 'klaar':
        return orderList.filter(o => o.status === 'klaar');
      case 'onderweg':
        return orderList.filter(o => o.status === 'onderweg');
      case 'completed':
        return orderList.filter(o =>
          ['bezorgd', 'voltooid', 'geannuleerd'].includes(o.status) && o.placed_at >= today
        );
      case 'archive': {
        if (archiveSearchResults !== null) return archiveSearchResults;
        const todayStr = new Date().toISOString().split('T')[0];
        return orderList.filter(o =>
          ['bezorgd', 'voltooid', 'geannuleerd'].includes(o.status) &&
          (o.placed_at ? o.placed_at.split('T')[0] : '') !== todayStr
        );
      }
      default:
        return orderList;
    }
  };

  const getCountdownTime = (deliveryTime) => {
    const diff = new Date(deliveryTime) - new Date();
    const isLate = diff < 0;
    const abs = Math.abs(diff);
    const minutes = Math.floor(abs / 60000);
    const hours = Math.floor(minutes / 60);
    const rem = minutes % 60;
    if (isLate) {
      return hours > 0 ? `-${hours}u ${rem}m te laat` : `${minutes}m te laat`;
    }
    if (minutes === 0) return 'Nu!';
    return hours > 0 ? `nog ${hours}u ${rem}m` : `nog ${minutes}m`;
  };

  const getStatusText = (status) => {
    const map = {
      unpaid: 'Niet betaald',
      nieuw: 'Betaald',
      bereiding: 'In bereiding',
      klaar: 'Klaar',
      onderweg: 'Onderweg',
      bezorgd: 'Bezorgd',
      voltooid: 'Afgehaald',
      geannuleerd: 'Geannuleerd',
      unpaid_deleted: 'Verwijderd',
    };
    return map[status] || status;
  };

  const formatOrderCode = (orderCode) => {
    if (!orderCode) return '#???';
    const parts = orderCode.split('-');
    return `#${parts[parts.length - 1]}`;
  };

  const getDeliveryInfo = (order) => {
    if (order.delivered_at && ['bezorgd', 'voltooid'].includes(order.status)) {
      return new Date(order.delivered_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    }
    if (order.delivery_time) {
      return new Date(order.delivery_time).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    }
    if (order.is_asap && order.placed_at) {
      return new Date(new Date(order.placed_at).getTime() + 60 * 60 * 1000)
        .toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    }
    return 'Onbekend';
  };

  const getDeliveryStatusIcon = (order) => {
    return null;
  };

  const openRouteInMaps = (order) => {
    if (!order.delivery_address) return;
    const address = `${order.delivery_address}, ${order.delivery_postcode} ${order.delivery_city}`;
    const enc = encodeURIComponent(address);
    const navApp = localStorage.getItem('kmfc-nav-app') || 'apple';
    let url;
    if (navApp === 'google') url = `https://www.google.com/maps/search/?api=1&query=${enc}`;
    else if (navApp === 'waze') url = `https://waze.com/ul?q=${enc}`;
    else url = `maps://?q=${enc}`;
    window.open(url, '_blank');
  };

  const handlePrintOrder = () => {
    if (!selectedOrder) return;
    const formatStatus = (s) => ({ pending: 'Wachten op bevestiging', preparing: 'Aan het bereiden', ready: 'Klaar', out_for_delivery: 'Onderweg', delivered: 'Bezorgd', picked_up: 'Afgehaald', cancelled: 'Geannuleerd' }[s] || s);
    const getOrderNumber = (id) => `#${id.toString().padStart(4, '0')}`;
    const getDelInfo = (o) => o.delivery_type === 'delivery' ? (o.driving_time || 'Z.S.M.') : 'Afhalen';
    const html = generatePrintHTML(selectedOrder, formatStatus, getOrderNumber, getDelInfo);
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.onload = () => { setTimeout(() => { win.print(); }, 500); };
  };

  const handleQuickAction = async (e, order, action) => {
    e.stopPropagation();
    const statusMap = {
      accept: 'bereiding',
      klaar: 'klaar',
      onderweg: 'onderweg',
      bezorgd: order.delivery_type === 'delivery' ? 'bezorgd' : 'voltooid',
    };
    const newStatus = statusMap[action];
    if (!newStatus) return;

    try {
      const client = getKmfcClient();
      if (!client) { alert('Database connectie mislukt'); return; }

      const updateData = { status: newStatus };
      if (['bezorgd', 'voltooid'].includes(newStatus)) {
        updateData.delivered_at = new Date().toISOString();
      }

      const { error } = await client.from('orders').update(updateData).eq('id', order.id);
      if (error) { alert('Fout bij bijwerken status'); return; }

      // Lokale update + sound
      soundService.current.handleOrderEvent('UPDATE', order, { ...order, ...updateData });
      await DataSyncService.forceSync();
    } catch (err) {
      console.error('❌ Quick action fout:', err);
      alert('Fout bij bijwerken status');
    }
  };

  const openOrderDetails = async (order) => {
    try {
      const client = getKmfcClient();
      const { data: fullOrder, error } = await client
        .from('orders')
        .select(`id, order_code, status, payment_intent_id, subtotal_cents, delivery_fee_cents, 
          tip_cents, total_cents, discount_cents, placed_at, customer_name, customer_phone, 
          customer_email, delivery_type, delivery_time, is_asap, delivery_notes, delivery_km,
          driving_time, delivery_address, delivery_postcode, delivery_city, delivered_at,
          billing_street, billing_postcode, billing_city, billing_house_number, billing_addition, billing_country`)
        .eq('id', order.id)
        .single();

      if (error) throw error;

      const { data: items } = await client
        .from('order_items')
        .select('id, product_id, name_snapshot, qty, unit_price_cents, line_total_cents, item_options')
        .eq('order_id', order.id);

      fullOrder.order_items = (items || []).map(item => ({
        ...item,
        parsed_options: (item.item_options || '').split('+').map(o => o.trim()).filter(Boolean),
      }));

      const productIds = fullOrder.order_items.map(i => i.product_id).filter(Boolean);
      if (productIds.length > 0) {
        const { data: products } = await client.from('products').select('id, Productnummer').in('id', productIds);
        if (products) {
          fullOrder.order_items = fullOrder.order_items.map(item => ({
            ...item,
            products: products.find(p => p.id === item.product_id) || null,
          }));
        }
      }

      setSelectedOrder(fullOrder);
      setSelectedStatus(fullOrder.status);
      setDetailsModalVisible(true);
    } catch (err) {
      console.error('Error loading details:', err);
      setSelectedOrder(order);
      setSelectedStatus(order.status);
      setDetailsModalVisible(true);
    }
  };

  const closeModal = () => {
    setDetailsModalVisible(false);
    setSelectedOrder(null);
    setSelectedStatus('');
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const { error } = await getKmfcClient().from('orders').update({ status: newStatus }).eq('id', orderId);
      if (error) { alert('Fout bij bijwerken status'); return; }
      setSelectedStatus(newStatus);
      if (selectedOrder) setSelectedOrder({ ...selectedOrder, status: newStatus });
      console.log('🔄 Order status updated - triggering refresh');
      setLastUpdateTime(new Date()); // Direct update van tijd
      await DataSyncService.forceSync();
    } catch (err) {
      console.error('Error:', err);
      alert('Fout bij bijwerken status');
    }
  };

  const updateInternalNotes = async (orderId, notes) => {
    try {
      await getKmfcClient().from('orders').update({ interne_notitie: notes }).eq('id', orderId);
    } catch (err) {
      console.error('Error notes:', err);
    }
  };

  const confirmDeleteOrder = () => {
    if (!selectedOrder) return;
    setOrderToDelete(selectedOrder);
    setDeleteConfirmed(false);
    setShowDeleteConfirmModal(true);
  };

  const toggleTestOrder = async (order) => {
    try {
      const newVal = !order.is_test;
      await getKmfcClient().from('orders').update({ is_test: newVal }).eq('id', order.id);
      if (selectedOrder) setSelectedOrder({ ...selectedOrder, is_test: newVal });
      await DataSyncService.forceSync();
    } catch (err) {
      console.error('Error toggling test:', err);
    }
  };

  const confirmDeleteUnpaidOrder = (order) => {
    setOrderToDelete(order);
    setDeleteConfirmed(false);
    setShowDeleteUnpaidConfirmModal(true);
  };

  const confirmMarkAsPaid = (order) => {
    setOrderToMarkAsPaid(order);
    setMarkAsPaidConfirmed(false);
    setShowMarkAsPaidModal(true);
  };

  const markAsPaidOrder = async () => {
    if (!orderToMarkAsPaid || !markAsPaidConfirmed) return;
    try {
      const { error } = await getKmfcClient().from('orders').update({ status: 'nieuw' }).eq('id', orderToMarkAsPaid.id);
      if (error) { setStatus({ type: 'error', message: error.message }); return; }
      setShowMarkAsPaidModal(false);
      setOrderToMarkAsPaid(null);
      setDetailsModalVisible(false);
      setSelectedOrder(null);
      await DataSyncService.forceSync();
      setStatus({ type: 'success', message: `Order ${formatOrderCode(orderToMarkAsPaid.order_code)} gemarkeerd als betaald` });
    } catch (err) {
      setStatus({ type: 'error', message: 'Onverwachte fout' });
    }
  };

  const deleteUnpaidOrder = async () => {
    if (!orderToDelete || !deleteConfirmed) return;
    try {
      const { error } = await getKmfcClient().from('orders').update({ status: 'unpaid_deleted' }).eq('id', orderToDelete.id);
      if (error) { setStatus({ type: 'error', message: error.message }); return; }
      setShowDeleteUnpaidConfirmModal(false);
      setOrderToDelete(null);
      setDetailsModalVisible(false);
      setSelectedOrder(null);
      await DataSyncService.forceSync();
      setStatus({ type: 'success', message: `Order ${formatOrderCode(orderToDelete.order_code)} verwijderd` });
    } catch (err) {
      setStatus({ type: 'error', message: 'Onverwachte fout' });
    }
  };

  const deleteOrder = async () => {
    if (!orderToDelete || !deleteConfirmed) return;
    const code = orderToDelete.order_code;
    try {
      const client = getKmfcClient();
      await client.from('order_items').delete().eq('order_id', orderToDelete.id);
      const { error } = await client.from('orders').delete().eq('id', orderToDelete.id);
      if (error) { setStatus({ type: 'error', message: error.message }); return; }
      setShowDeleteConfirmModal(false);
      setDeleteConfirmed(false);
      setOrderToDelete(null);
      setDetailsModalVisible(false);
      setSelectedOrder(null);
      await DataSyncService.forceSync();
      setStatus({ type: 'success', message: `Order ${formatOrderCode(code)} verwijderd` });
    } catch (err) {
      console.error('Delete error:', err);
      setStatus({ type: 'error', message: 'Onverwachte fout bij verwijderen' });
    }
  };

  const createTestOrder = () => setShowTestOrderModal(true);

  const confirmTestOrder = async () => {
    setIsCreatingTestOrder(true);
    setShowTestOrderModal(false);
    try {
      const client = getKmfcClient();
      const testOrder = {
        customer_name: 'Test Klant', customer_phone: '0612345678', customer_email: 'test@example.com',
        delivery_type: testOrderType, subtotal_cents: 150000,
        delivery_fee_cents: testOrderType === 'delivery' ? 500 : 0,
        tip_cents: 100, total_cents: testOrderType === 'delivery' ? 150600 : 150100,
        status: 'bereiding', is_test: true, placed_at: new Date().toISOString(),
        delivery_notes: `Test order (${testOrderType}) - 100 producten`,
        order_code: 'TEST-' + Date.now(),
        delivery_address: testOrderType === 'delivery' ? 'Teststraat 123' : null,
        delivery_postcode: testOrderType === 'delivery' ? '1234 AB' : null,
        delivery_city: testOrderType === 'delivery' ? 'Teststad' : null,
      };
      const { data: orderData, error: orderError } = await client.from('orders').insert([testOrder]).select();
      if (orderError) { setStatus({ type: 'error', message: orderError.message }); return; }

      const items = Array.from({ length: 100 }, (_, i) => ({
        order_id: orderData[0].id,
        name_snapshot: `Test Product ${i + 1}`,
        qty: 1, unit_price_cents: 1500, line_total_cents: 1500,
        item_options: i % 2 === 0 ? 'Extra saus' : 'Zonder ui',
      }));
      await client.from('order_items').insert(items);
      setStatus({ type: 'success', message: `Test order aangemaakt (${testOrderType.toUpperCase()})` });
      await DataSyncService.forceSync();
    } catch (err) {
      setStatus({ type: 'error', message: 'Fout bij aanmaken test order' });
    } finally {
      setIsCreatingTestOrder(false);
      setTestOrderType('pickup');
    }
  };

  const TAB_LABELS = { new: 'Nieuw', bereiding: 'Bereiding', klaar: 'Klaar', onderweg: 'Onderweg', completed: 'Voltooid', archive: 'Archief' };
  const getEmptyState = (tab) => ({
    icon: <XCircle size={44} strokeWidth={1.5} />,
    title: `Geen bestellingen met de status "${TAB_LABELS[tab] || tab}"`,
    sub: '',
  });

  const exportCsv = async () => {
    setIsExportingCsv(true);
    try {
      const client = getKmfcClient();
      const since = new Date(archiveStartDate).toISOString();
      const before = new Date(new Date(archiveEndDate).getTime() + 86400000).toISOString();
      const { data: ordersData, error } = await client
        .from('orders')
        .select('id,order_code,status,placed_at,customer_name,customer_phone,customer_email,delivery_type,delivery_address,delivery_postcode,delivery_city,delivery_time,is_asap,delivery_notes,interne_notitie,subtotal_cents,delivery_fee_cents,tip_cents,total_cents')
        .gte('placed_at', since)
        .lt('placed_at', before)
        .eq('is_test', false)
        .order('placed_at', { ascending: true })
        .limit(10000);
      if (error) throw error;

      const ids = ordersData.map(o => o.id);
      let itemsMap = {};
      if (ids.length > 0) {
        const { data: items } = await client.from('order_items').select('order_id,name_snapshot,qty,item_options').in('order_id', ids);
        (items || []).forEach(item => {
          if (!itemsMap[item.order_id]) itemsMap[item.order_id] = [];
          itemsMap[item.order_id].push(item);
        });
      }

      const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' }) : '';
      const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Amsterdam' }) : '';
      const fmtEUR = (cents) => `€${((cents || 0) / 100).toFixed(2).replace('.', ',')}`;
      const esc = (s) => { const str = String(s || ''); return (str.includes(';') || str.includes('"') || str.includes('\n')) ? `"${str.replace(/"/g, '""')}"` : str; };

      const headers = ['Ordernummer','Datum','Tijd','Status','Type','Naam','Telefoon','E-mail','Adres','Postcode','Stad','Bezorgtijd','ASAP','Artikelen','Subtotaal','Bezorgkosten','Fooi','Totaal','Notities klant','Interne notitie'];
      const rows = ordersData.map(o => {
        const artikelen = (itemsMap[o.id] || []).map(i => `${i.qty}x ${i.name_snapshot || '?'}${i.item_options ? ` (${i.item_options})` : ''}`).join(' | ');
        return [o.order_code || '', fmtDate(o.placed_at), fmtTime(o.placed_at), o.status || '', o.delivery_type === 'delivery' ? 'Bezorging' : 'Afhaal', o.customer_name || '', o.customer_phone || '', o.customer_email || '', o.delivery_address || '', o.delivery_postcode || '', o.delivery_city || '', o.delivery_time ? fmtTime(o.delivery_time) : '', o.is_asap ? 'Ja' : 'Nee', artikelen, fmtEUR(o.subtotal_cents), fmtEUR(o.delivery_fee_cents), fmtEUR(o.tip_cents), fmtEUR(o.total_cents), o.delivery_notes || '', o.interne_notitie || ''].map(esc).join(';');
      });
      const csv = [headers.map(esc).join(';'), ...rows].join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bestellingen-${archiveStartDate}-tm-${archiveEndDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export fout:', err);
      alert('Export mislukt: ' + err.message);
    } finally {
      setIsExportingCsv(false);
    }
  };

  if (loading) return <WebvonkLoader fullscreen text="Bestellingen worden geladen..." />;

  const effectiveConnectionState = simulateConnectionState || (
    lastUpdateTime && (Date.now() - lastUpdateTime.getTime()) > 10 * 60 * 1000 ? 'offline' : null
  );

  return (
    <Container>
      <RedBanner unpaidOrdersCount={0} />
      <NotificationPopup />
      <NotificationModal />


      <Content>
        <PageHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4 }}>
          <h1>Bestellingen</h1>
            {effectiveConnectionState === 'standby' && (
              <InfoBadge>
                <Info size={11} />Standby
                <InfoTooltip>Bestellingen worden opgehaald zodra het restaurant opent.</InfoTooltip>
              </InfoBadge>
            )}
            {effectiveConnectionState === 'offline' && (
              <InfoBadge $color="#ef4444">
                <Info size={11} />Offline
                <InfoTooltip>Geen verbinding. Foutcode: {connectionErrorCode || 'onbekend'}</InfoTooltip>
              </InfoBadge>
            )}
            {(showSoundLabel || hoverSoundIcon || tabCounts.new > 0) && (
              <span
                onMouseEnter={() => setHoverSoundIcon(true)}
                onMouseLeave={() => setHoverSoundIcon(false)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, cursor: 'default' }}
              >
                {!soundEnabled
                  ? <VolumeX size={22} style={{ color: '#ef4444', flexShrink: 0 }} />
                  : <Volume2 size={22} style={{ color: tabCounts.new > 0 ? '#f97316' : '#9ca3af', flexShrink: 0 }} />
                }
                {(showSoundLabel || hoverSoundIcon) && (
                  <span style={{ fontSize: 13, fontWeight: 400, color: '#9ca3af', letterSpacing: 0 }}>
                    {soundError
                      ? 'Het geluid is uitgeschakeld door een technische fout'
                      : !soundEnabled
                      ? 'Het geluid is uitgeschakeld'
                      : tabCounts.new > 0
                      ? 'Het geluid speelt af'
                      : 'Het geluid is ingeschakeld'}
                  </span>
                )}
              </span>
            )}
          </div>
        </PageHeader>

        <NavBar style={{ marginTop: 12 }}>
          <NavTab active={activeTab === 'new'} accentcolor="#FF9500" onClick={() => setActiveTab('new')}>
            <AlarmClock size={13} /> Nieuw
            {tabCounts.new > 0 && <TabCounter active={activeTab === 'new'}>{tabCounts.new}</TabCounter>}
            {tabCounts.testNew > 0 && <TestCounter>{tabCounts.testNew}</TestCounter>}
          </NavTab>
          <NavTab active={activeTab === 'bereiding'} accentcolor="#FF3B30" onClick={() => setActiveTab('bereiding')}>
            <Flame size={13} /> Bereiden
            {tabCounts.preparing > 0 && <TabCounter active={activeTab === 'bereiding'}>{tabCounts.preparing}</TabCounter>}
          </NavTab>
          <NavTab active={activeTab === 'klaar'} accentcolor="#30D158" onClick={() => setActiveTab('klaar')}>
            <CheckCircle2 size={13} /> Klaar
            {tabCounts.ready > 0 && <TabCounter active={activeTab === 'klaar'}>{tabCounts.ready}</TabCounter>}
          </NavTab>
          <NavTab active={activeTab === 'onderweg'} accentcolor="#0A84FF" onClick={() => setActiveTab('onderweg')}>
            <Bike size={13} /> Onderweg
            {tabCounts.out_for_delivery > 0 && <TabCounter active={activeTab === 'onderweg'}>{tabCounts.out_for_delivery}</TabCounter>}
          </NavTab>
          <NavTab active={activeTab === 'completed'} accentcolor="#30D158" onClick={() => setActiveTab('completed')}>
            <BadgeCheck size={13} /> Voltooid
            {tabCounts.completed > 0 && <TabCounter active={activeTab === 'completed'}>{tabCounts.completed}</TabCounter>}
          </NavTab>
        </NavBar>

        <>

        {activeTab === 'archive' && (
          <SearchContainer style={{ marginBottom: 8, marginTop: 10, borderRadius: 12 }}>
            <Search size={14} style={{ color: colors.muted, opacity: 0.5, flexShrink: 0 }} />
            <SearchInput
              placeholder="Zoek op naam of ordernummer…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: colors.muted, opacity: 0.5 }}
              >
                <X size={15} />
              </button>
            )}
          </SearchContainer>
        )}

        <OrdersGrid key={activeTab}>
            {(() => {
              const q = searchQuery.trim().toLowerCase();
              const filteredOrders = q
                ? orders.filter(o =>
                    (o.customer_name || '').toLowerCase().includes(q) ||
                    (o.order_code || '').toLowerCase().includes(q)
                  )
                : orders;

              if (filteredOrders.length === 0) {
                return (
                  <EmptyState>
                    <EmptyIcon>{q ? <Search size={44} strokeWidth={1.5} /> : getEmptyState(activeTab).icon}</EmptyIcon>
                    <EmptyTitle>
                      {q
                        ? `Geen bestellingen gevonden voor "${searchQuery}"`
                        : getEmptyState(activeTab).title}
                    </EmptyTitle>
                    <EmptySub>{getEmptyState(activeTab).sub}</EmptySub>
                  </EmptyState>
                );
              }

              return filteredOrders.map(order => {
                const isUnpaid = ['unpaid', 'payment_failed', 'payment_pending', 'payment_cancelled'].includes(order.status);
                return (
                  <OrderBlock key={order.id} data-order-id={order.id} status={order.status} isUnpaid={isUnpaid} onClick={() => openOrderDetails(order)}>
                    {/* Row 1: name + order# */}
                    <BlockHeader>
                      <HeaderLeft>
                        <CustomerName>{order.customer_name || 'Gast'}</CustomerName>
                        {isUnpaid && <UnpaidBadge>Onbetaald</UnpaidBadge>}
                        {order.is_test && <TestBadge>TEST</TestBadge>}
                      </HeaderLeft>
                      <HeaderRight>
                        <OrderNumber>{formatOrderCode(order.order_code)}</OrderNumber>
                      </HeaderRight>
                    </BlockHeader>
                    {/* Row 2: icon + info + items badge + action */}
                    <CardRow>
                      <DeliveryIcon>
                        {order.delivery_type === 'delivery' ? <Truck size={18} /> : <ShoppingBag size={18} />}
                      </DeliveryIcon>
                      <TimeAndIcon>
                        {order.delivery_type === 'delivery' && order.delivery_city
                          ? <span style={{ marginRight: 4 }}>{order.delivery_city}</span>
                          : null}
                        <ZSMTime isZSM={!order.delivery_time && order.is_asap} style={{ color: 'inherit' }}>
                          {!order.delivery_time && order.is_asap ? 'Z.S.M.' : getDeliveryInfo(order)}
                        </ZSMTime>
                      </TimeAndIcon>
                      {/* Items badge */}
                      {(() => {
                        const qty = order.total_items_qty || order.items_qty || (order.order_items?.length) || 0;
                        return qty > 0 ? (
                          <ItemsBadge>{qty} art.</ItemsBadge>
                        ) : null;
                      })()}
                      {/* Quick action buttons */}
                      {order.status === 'nieuw' && (
                        <>
                          <BlockButton onClick={(e) => handleQuickAction(e, order, 'accept')}><ArrowRight size={15} /></BlockButton>
                        </>
                      )}
                      {order.status === 'bereiding' && (
                        <BlockButton onClick={(e) => handleQuickAction(e, order, 'klaar')}><ArrowRight size={15} /></BlockButton>
                      )}
                      {order.status === 'klaar' && (
                        <>
                          {order.delivery_type === 'delivery' && order.delivery_address && (
                            <RouteButton onClick={(e) => { e.stopPropagation(); openRouteInMaps(order); }}>
                              <Navigation size={12} /> Route
                            </RouteButton>
                          )}
                          <BlockButton onClick={(e) => handleQuickAction(e, order, order.delivery_type === 'delivery' ? 'onderweg' : 'bezorgd')}><ArrowRight size={15} /></BlockButton>
                        </>
                      )}
                      {order.status === 'onderweg' && (
                        <BlockButton onClick={(e) => handleQuickAction(e, order, 'bezorgd')}><ArrowRight size={15} /></BlockButton>
                      )}
                      {isUnpaid && (
                        <>
                          <DeleteButton onClick={(e) => { e.stopPropagation(); confirmDeleteUnpaidOrder(order); }}><Trash2 size={14} /></DeleteButton>
                          <BlockButton style={{ background: 'rgba(34,197,94,0.15)', color: '#16a34a', border: '1px solid rgba(34,197,94,0.3)' }} onClick={(e) => { e.stopPropagation(); confirmMarkAsPaid(order); }}><CheckCircle size={14} /></BlockButton>
                        </>
                      )}
                    </CardRow>
                    {/* Countdown row (like Swift's countdownRow) */}
                    {timer[order.id] && !['completed', 'voltooid', 'bezorgd', 'geannuleerd'].includes(order.status) && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: 12, fontWeight: timer[order.id].includes('te laat') ? 700 : 500,
                        color: timer[order.id].includes('te laat') ? '#ff3b30' : timer[order.id] === 'Nu!' ? '#ff9500' : 'rgba(128,128,128,0.6)',
                      }}>
                        {timer[order.id].includes('te laat')
                          ? <span style={{ fontSize: 11, fontWeight: 700, color: '#ff3b30' }}>⚠</span>
                          : <span style={{ fontSize: 11, opacity: 0.5 }}>⏱</span>
                        }
                        {timer[order.id]}
                      </div>
                    )}
                    {isUnpaid && (
                      <PaidButton onClick={(e) => { e.stopPropagation(); confirmMarkAsPaid(order); }}>Markeer als betaald</PaidButton>
                    )}
                  </OrderBlock>
                );
              });
            })()}
          </OrdersGrid>
        {activeTab === 'completed' && (
          <div style={{ textAlign: 'center', fontSize: 13, opacity: 0.4, marginTop: 14, padding: '0 16px' }}>
            Oudere bestellingen staan in het Archief tabblad
          </div>
        )}
        </>

      </Content>

      {/* Order Details Modal */}
      <Modal visible={detailsModalVisible} onClick={closeModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Order {formatOrderCode(selectedOrder?.order_code)}</ModalTitle>
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <CloseButton onClick={handlePrintOrder}>
                <Printer size={18} />
              </CloseButton>
              <CloseButton onClick={closeModal}><X size={20} /></CloseButton>
            </div>
          </ModalHeader>

          {selectedOrder && (() => {
            const o = selectedOrder;
            const isDelivery = o.delivery_type === 'delivery';
            const isUnpaid = ['unpaid', 'payment_failed', 'payment_pending', 'payment_cancelled'].includes(o.status);
            const nextStatusMap = { nieuw: { status: 'bereiding', label: 'Start bereiding' }, bereiding: { status: 'klaar', label: 'Markeer als klaar' }, klaar: { status: isDelivery ? 'onderweg' : 'voltooid', label: isDelivery ? 'Start bezorging' : 'Markeer als afgehaald' }, onderweg: { status: 'bezorgd', label: 'Markeer als bezorgd' } };
            const nextStep = nextStatusMap[o.status];
            const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : null;
            const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
            const fmtEUR = (cents) => `€${((cents || 0) / 100).toFixed(2).replace('.', ',')}`;
            return (
              <ModalBody>
                <ModalGrid>
                  {/* Timing card */}
                  <DetailCard>
                    <DetailSectionLabel>Tijdstip</DetailSectionLabel>
                    <DetailInfoRow>
                      <DetailRowIcon>{isDelivery ? <Truck size={15} /> : <ShoppingBag size={15} />}</DetailRowIcon>
                      <DetailRowContent>
                        <DetailRowLabel>Type</DetailRowLabel>
                        <DetailRowValue>{isDelivery ? 'Bezorging' : 'Afhalen'}</DetailRowValue>
                      </DetailRowContent>
                      {o.is_test && <span style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>TEST</span>}
                    </DetailInfoRow>
                    <DetailInfoRow>
                      <DetailRowIcon><Clock size={15} /></DetailRowIcon>
                      <DetailRowContent>
                        <DetailRowLabel>{isDelivery ? 'Bezorgmoment' : 'Afhaalmoment'}</DetailRowLabel>
                        {o.delivery_time ? (
                          <>
                            <DetailRowValue>
                              {fmtTime(o.delivery_time)}
                              {new Date(o.delivery_time).toDateString() !== new Date().toDateString() && <span style={{ color: colors.muted, fontWeight: 400, marginLeft: 6 }}>{fmtDate(o.delivery_time)}</span>}
                            </DetailRowValue>
                          </>
                        ) : o.is_asap ? (
                          <>
                            <DetailRowValue style={{ fontStyle: 'italic' }}>
                              Zo snel mogelijk
                              {o.placed_at && (
                                <span style={{ fontStyle: 'normal', fontWeight: 400, color: colors.muted, marginLeft: 6 }}>
                                  ({fmtTime(new Date(new Date(o.placed_at).getTime() + 60 * 60 * 1000))})
                                </span>
                              )}
                            </DetailRowValue>
                            {o.placed_at && new Date(o.placed_at).toDateString() !== new Date().toDateString() && <div style={{ fontSize: 11, color: colors.muted, marginTop: 2 }}>{fmtDate(o.placed_at)}</div>}
                          </>
                        ) : <DetailRowValue style={{ color: colors.muted }}>Onbekend</DetailRowValue>}
                      </DetailRowContent>
                    </DetailInfoRow>
                    {o.placed_at && (
                      <DetailInfoRow>
                        <DetailRowIcon><Receipt size={15} /></DetailRowIcon>
                        <DetailRowContent>
                          <DetailRowLabel>Besteld op</DetailRowLabel>
                          <DetailRowValue>{fmtTime(o.placed_at)}, {fmtDate(o.placed_at)}</DetailRowValue>
                        </DetailRowContent>
                      </DetailInfoRow>
                    )}
                    {o.delivered_at && (
                      <DetailInfoRow>
                        <DetailRowIcon><CheckCircle size={15} /></DetailRowIcon>
                        <DetailRowContent>
                          <DetailRowLabel>{isDelivery ? 'Bezorgd om' : 'Afgehaald om'}</DetailRowLabel>
                          <DetailRowValue>{fmtTime(o.delivered_at)}</DetailRowValue>
                        </DetailRowContent>
                      </DetailInfoRow>
                    )}
                    {isDelivery && (o.delivery_km || o.driving_time) && (
                      <DetailInfoRow>
                        <DetailRowIcon><Navigation size={15} /></DetailRowIcon>
                        <DetailRowContent>
                          <DetailRowLabel>Route</DetailRowLabel>
                          <DetailRowValue>
                            {[o.delivery_km && `${parseFloat(o.delivery_km).toFixed(1)} km`, o.driving_time && `${o.driving_time} min rijtijd`].filter(Boolean).join(' · ')}
                          </DetailRowValue>
                        </DetailRowContent>
                      </DetailInfoRow>
                    )}
                  </DetailCard>

                  {/* Klant card */}
                  <DetailCard>
                    <DetailSectionLabel>Klant</DetailSectionLabel>
                    <DetailInfoRow>
                      <DetailRowIcon><User size={15} /></DetailRowIcon>
                      <DetailRowContent>
                        <DetailRowLabel>Naam</DetailRowLabel>
                        <DetailRowValue style={{ fontWeight: 600 }}>{o.customer_name || 'Gast'}</DetailRowValue>
                      </DetailRowContent>
                    </DetailInfoRow>
                    {o.customer_phone && (
                      <DetailInfoRow>
                        <DetailRowIcon><Phone size={15} /></DetailRowIcon>
                        <DetailRowContent>
                          <DetailRowLabel>Telefoon</DetailRowLabel>
                          <DetailRowValue><a href={`tel:${o.customer_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{o.customer_phone}</a></DetailRowValue>
                        </DetailRowContent>
                      </DetailInfoRow>
                    )}
                    {o.customer_email && (
                      <DetailInfoRow>
                        <DetailRowIcon><Mail size={15} /></DetailRowIcon>
                        <DetailRowContent>
                          <DetailRowLabel>Email</DetailRowLabel>
                          <DetailRowValue style={{ fontSize: 13 }}>{o.customer_email}</DetailRowValue>
                        </DetailRowContent>
                      </DetailInfoRow>
                    )}
                    {isDelivery && o.delivery_address && (
                      <DetailInfoRow>
                        <DetailRowIcon><MapPin size={15} /></DetailRowIcon>
                        <DetailRowContent>
                          <DetailRowLabel>Adres</DetailRowLabel>
                          <DetailRowValue style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationColor: colors.border }} onClick={() => openRouteInMaps(o)}>
                            {o.delivery_address}, {o.delivery_postcode} {o.delivery_city}
                          </DetailRowValue>
                        </DetailRowContent>
                      </DetailInfoRow>
                    )}
                  </DetailCard>

                  {/* Status wijzigen card */}
                  <DetailCard>
                    <DetailSectionLabel>Status wijzigen</DetailSectionLabel>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={{ flex: 1, padding: '8px 10px', background: colors.surface, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 10, fontSize: 14, cursor: 'pointer', colorScheme: themeMode }}>
                        <option value="nieuw">Betaald</option>
                        <option value="bereiding">In bereiding</option>
                        <option value="klaar">Klaar</option>
                        <option value="onderweg">Onderweg</option>
                        <option value="bezorgd">Bezorgd</option>
                        <option value="voltooid">Afgehaald</option>
                        <option value="geannuleerd">Geannuleerd</option>
                      </select>
                      <button onClick={() => updateOrderStatus(o.id, selectedStatus)} style={{ padding: '8px 14px', background: colors.surface, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Opslaan</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {canSeeTestToggle && (
                        <button onClick={() => toggleTestOrder(o)} style={{ padding: '9px 12px', background: o.is_test ? 'rgba(234,179,8,0.1)' : 'transparent', border: `1px solid ${o.is_test ? 'rgba(234,179,8,0.35)' : colors.border}`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, color: o.is_test ? '#ca8a04' : colors.muted }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Beaker size={14} /> Testorder</span>
                          <span style={{ width: 36, height: 20, borderRadius: 10, background: o.is_test ? '#eab308' : colors.border, display: 'flex', alignItems: 'center', padding: '0 3px', transition: 'background 0.2s', flexShrink: 0 }}>
                            <span style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transform: o.is_test ? 'translateX(16px)' : 'translateX(0)', transition: 'transform 0.2s', display: 'block' }} />
                          </span>
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={confirmDeleteOrder} style={{ padding: '9px 12px', background: 'rgba(220,38,38,0.08)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          <Trash2 size={14} /> Permanent verwijderen
                        </button>
                      )}
                    </div>
                  </DetailCard>

                  {/* Artikelen + Bedragen card */}
                  <DetailCard style={{ gridColumn: '1 / -1' }}>
                    <DetailSectionLabel>Artikelen ({o.order_items?.length || 0})</DetailSectionLabel>
                    {!o.order_items || o.order_items.length === 0 ? (
                      <div style={{ color: colors.muted, fontSize: 13 }}>Geen artikelen gevonden</div>
                    ) : (
                      o.order_items.map((item, index) => {
                        const filteredOptions = item.parsed_options?.filter(opt => !opt.includes('upsell')) || [];
                        return (
                          <ItemRow key={index}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {item.products?.Productnummer && (
                                <span style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted, borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: 700, flexShrink: 0, minWidth: 28, textAlign: 'center' }}>{item.products.Productnummer}</span>
                              )}
                              <span style={{ fontWeight: 600, fontSize: 14, flex: 1, color: colors.text, lineHeight: 1.3 }}>{item.name_snapshot}</span>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 1 }}>
                                <span style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>{fmtEUR(item.line_total_cents)}</span>
                                <span style={{ color: colors.muted, fontSize: 13 }}>{item.qty}× {fmtEUR(item.unit_price_cents || Math.round(item.line_total_cents / item.qty))}</span>
                              </div>
                            </div>
                            {filteredOptions.length > 0 && (
                              <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {(() => {
                                  const remarks = filteredOptions.filter(o => o.startsWith('!'));
                                  const options = filteredOptions.filter(o => !o.startsWith('!'));
                                  return (
                                    <>
                                      {options.length > 0 && (
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                          <Tag size={12} color={colors.muted} style={{ marginTop: 2, flexShrink: 0, opacity: 0.6 }} />
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 6px' }}>
                                            {options.map((opt, i) => (
                                              <span key={i} style={{ fontSize: 11, color: colors.muted, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '1px 6px' }}>{opt}</span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {remarks.length > 0 && (
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                          <MessageSquare size={12} color="#f97316" style={{ marginTop: 2, flexShrink: 0 }} />
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 6px' }}>
                                            {remarks.map((opt, i) => (
                                              <span key={i} style={{ fontSize: 11, color: '#f97316', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 4, padding: '1px 6px' }}>{opt.substring(1)}</span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </ItemRow>
                        );
                      })
                    )}
                    <div style={{ paddingTop: 12, borderTop: `1px solid ${colors.border}` }}>
                      <DetailMoneyRow><span>Subtotaal</span><span>{fmtEUR(o.subtotal_cents)}</span></DetailMoneyRow>
                      {(o.delivery_fee_cents || 0) > 0 && <DetailMoneyRow><span>Bezorging</span><span>{fmtEUR(o.delivery_fee_cents)}</span></DetailMoneyRow>}
                      {(o.tip_cents || 0) > 0 && <DetailMoneyRow><span>Fooi</span><span>{fmtEUR(o.tip_cents)}</span></DetailMoneyRow>}
                      <DetailMoneyRow total><span>Totaal</span><span>{fmtEUR(o.total_cents)}</span></DetailMoneyRow>
                    </div>
                    {o.payment_intent_id && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
                        <DetailInfoRow style={{ paddingTop: 0 }}>
                          <DetailRowIcon><CreditCard size={15} /></DetailRowIcon>
                          <DetailRowContent>
                            <DetailRowLabel>Payment intent</DetailRowLabel>
                            <div
                              onClick={() => { navigator.clipboard.writeText(o.payment_intent_id); setCopiedPaymentId(true); setTimeout(() => setCopiedPaymentId(false), 2000); }}
                              style={{ fontSize: 11, fontFamily: 'monospace', color: colors.muted, wordBreak: 'break-all', marginTop: 2, cursor: 'pointer', userSelect: 'none' }}
                              title="Klik om te kopiëren"
                            >
                              {o.payment_intent_id} ⧉
                            </div>
                          </DetailRowContent>
                        </DetailInfoRow>
                      </div>
                    )}
                  </DetailCard>

                  {/* Actie knop — full width */}
                  <div style={{ gridColumn: '1 / -1' }}>
                  {isUnpaid ? (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <DetailDangerButton onClick={(e) => { e.stopPropagation(); closeModal(); confirmDeleteUnpaidOrder(o); }} style={{ flex: 1 }}>
                        <Trash2 size={16} /> Verwijderen
                      </DetailDangerButton>
                      <DetailActionButton accent="#34C759" onClick={(e) => { e.stopPropagation(); closeModal(); confirmMarkAsPaid(o); }} style={{ flex: 1 }}>
                        <CheckCircle size={16} /> Markeer betaald
                      </DetailActionButton>
                    </div>
                  ) : nextStep ? (
                    <DetailActionButton onClick={(e) => { e.stopPropagation(); const actionKey = o.status === 'nieuw' ? 'accept' : o.status === 'bereiding' ? 'klaar' : o.status === 'klaar' ? (isDelivery ? 'onderweg' : 'bezorgd') : 'bezorgd'; handleQuickAction(e, o, actionKey); closeModal(); }}>
                      {nextStep.label} →
                    </DetailActionButton>
                  ) : null}
                  </div>
                </ModalGrid>
              </ModalBody>
            );
          })()}
        </ModalContent>
      </Modal>

      {copiedPaymentId && (
        <div style={{
          position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)',
          background: themeMode === 'light' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(30px) saturate(180%)',
          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
          border: themeMode === 'light' ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.12)',
          borderRadius: 999, padding: '10px 22px',
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 13, fontWeight: 600, color: themeMode === 'light' ? '#111' : '#fff', letterSpacing: '0.02em',
          boxShadow: themeMode === 'light' ? '0 4px 20px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)' : '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
          zIndex: 9999, pointerEvents: 'none', whiteSpace: 'nowrap', userSelect: 'none',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={themeMode === 'light' ? '#111' : '#fff'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Gekopieerd
        </div>
      )}

      {/* Mark as Paid Modal */}
      {showMarkAsPaidModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }} onClick={() => { setShowMarkAsPaidModal(false); setMarkAsPaidConfirmed(false); }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '20px', maxWidth: '480px', width: '100%', padding: '32px', boxShadow: '0 32px 64px rgba(0,0,0,0.4)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '16px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22c55e' }}>
                <CheckCircle size={28} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: colors.text }}>Order Markeren als Betaald?</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: colors.muted, fontFamily: 'monospace' }}>Order {formatOrderCode(orderToMarkAsPaid?.order_code)}</p>
              </div>
            </div>
            <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#22c55e', lineHeight: '1.5' }}>
                De status wordt gewijzigd naar <strong>paid</strong>. De bestelling verschuift naar de betaalde bestellingen.
              </p>
            </div>
            <div style={{ background: colors.background, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', color: colors.text, lineHeight: '1.6' }}>
                <div><strong>Naam:</strong> {orderToMarkAsPaid?.customer_name || 'Gast'}</div>
                {orderToMarkAsPaid?.customer_phone && <div><strong>Telefoon:</strong> {orderToMarkAsPaid.customer_phone}</div>}
                {orderToMarkAsPaid?.total_cents && <div><strong>Bedrag:</strong> €{(orderToMarkAsPaid.total_cents / 100).toFixed(2)}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '24px', padding: '16px', background: colors.background, borderRadius: '12px', border: `1px solid ${colors.border}` }}>
              <input type="checkbox" id="markAsPaidConfirm" checked={markAsPaidConfirmed} onChange={(e) => setMarkAsPaidConfirmed(e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: '#22c55e' }} />
              <label htmlFor="markAsPaidConfirm" style={{ margin: 0, fontSize: '14px', color: colors.text, cursor: 'pointer' }}>Ik bevestig dat deze bestelling is betaald.</label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowMarkAsPaidModal(false); setMarkAsPaidConfirmed(false); }} style={{ padding: '12px 24px', background: colors.background, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Annuleren</button>
              <button onClick={markAsPaidOrder} disabled={!markAsPaidConfirmed} style={{ padding: '12px 24px', background: markAsPaidConfirmed ? '#22c55e' : 'rgba(34,197,94,0.3)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: markAsPaidConfirmed ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={16} /> Markeer als betaald
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Unpaid Modal */}
      {showDeleteUnpaidConfirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }} onClick={() => { setShowDeleteUnpaidConfirmModal(false); setDeleteConfirmed(false); }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '20px', maxWidth: '480px', width: '100%', padding: '32px', boxShadow: '0 32px 64px rgba(0,0,0,0.4)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '16px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                <Trash2 size={28} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: colors.text }}>Unpaid Order Verwijderen?</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: colors.muted, fontFamily: 'monospace' }}>Order {formatOrderCode(orderToDelete?.order_code)}</p>
              </div>
            </div>
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#ef4444', lineHeight: '1.5' }}>
                Status wordt gewijzigd naar <strong>unpaid_deleted</strong>. De bestelling is niet meer zichtbaar.
              </p>
            </div>
            <div style={{ background: colors.background, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', color: colors.text, lineHeight: '1.6' }}>
                <div><strong>Naam:</strong> {orderToDelete?.customer_name || 'Gast'}</div>
                {orderToDelete?.customer_phone && <div><strong>Telefoon:</strong> {orderToDelete.customer_phone}</div>}
                {orderToDelete?.total_cents && <div><strong>Bedrag:</strong> €{(orderToDelete.total_cents / 100).toFixed(2)}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '24px', padding: '16px', background: colors.background, borderRadius: '12px', border: `1px solid ${colors.border}` }}>
              <input type="checkbox" id="unpaidDeleteConfirm" checked={deleteConfirmed} onChange={(e) => setDeleteConfirmed(e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: '#ef4444' }} />
              <label htmlFor="unpaidDeleteConfirm" style={{ margin: 0, fontSize: '14px', color: colors.text, cursor: 'pointer' }}>Ik begrijp dat deze bestelling niet meer zichtbaar zal zijn.</label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowDeleteUnpaidConfirmModal(false); setDeleteConfirmed(false); }} style={{ padding: '12px 24px', background: colors.background, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Annuleren</button>
              <button onClick={deleteUnpaidOrder} disabled={!deleteConfirmed} style={{ padding: '12px 24px', background: deleteConfirmed ? '#dc2626' : 'rgba(220,38,38,0.3)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: deleteConfirmed ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Trash2 size={16} /> Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Order Modal */}
      {showDeleteConfirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: themeMode === 'light' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }} onClick={() => { setShowDeleteConfirmModal(false); setDeleteConfirmed(false); }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '20px', maxWidth: '480px', width: '100%', padding: '28px', boxShadow: themeMode === 'light' ? '0 24px 60px rgba(0,0,0,0.14)' : '0 24px 60px rgba(0,0,0,0.5)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', flexShrink: 0 }}>
                <Trash2 size={24} />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '19px', fontWeight: '700', color: colors.text }}>Bestelling verwijderen?</h2>
                <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: colors.muted, fontFamily: 'monospace' }}>Order {formatOrderCode(orderToDelete?.order_code)}</p>
              </div>
            </div>
            <p style={{ margin: '0 0 18px 0', fontSize: '14px', lineHeight: '1.6', color: colors.muted }}>
              Dit kan <strong style={{ color: '#dc2626' }}>niet</strong> worden teruggedraaid. Alle ordergegevens en items worden permanent verwijderd.
            </p>
            <div style={{ marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: themeMode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)', borderRadius: '12px', border: `1px solid ${colors.border}` }}>
              <input type="checkbox" id="deleteConfirm" checked={deleteConfirmed} onChange={(e) => setDeleteConfirmed(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#dc2626', cursor: 'pointer', flexShrink: 0 }} />
              <label htmlFor="deleteConfirm" style={{ margin: 0, fontSize: '14px', color: colors.text, cursor: 'pointer', lineHeight: 1.4 }}>Ik begrijp dat deze order permanent weg is en niet hersteld kan worden.</label>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowDeleteConfirmModal(false); setDeleteConfirmed(false); }} style={{ flex: 1, padding: '13px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: '12px', color: colors.muted, fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Annuleren</button>
              <button onClick={deleteOrder} disabled={!deleteConfirmed} style={{ flex: 1, padding: '13px', background: deleteConfirmed ? '#dc2626' : themeMode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '12px', color: deleteConfirmed ? 'white' : colors.muted, fontSize: '14px', fontWeight: '600', cursor: deleteConfirmed ? 'pointer' : 'not-allowed', boxShadow: deleteConfirmed ? '0 6px 20px rgba(220,38,38,0.35)' : 'none' }}>
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Closed Warning Modal */}
      {showClosedWarning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowClosedWarning(false)}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '16px', maxWidth: '500px', width: '90%', padding: '24px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: 700, color: colors.text, textAlign: 'center' }}>⚠️ Shop Gesloten</h2>
            <p style={{ margin: '0 0 24px 0', fontSize: '16px', color: colors.muted, lineHeight: '1.5', textAlign: 'center' }}>
              De shop is momenteel gesloten, maar volgens de ingestelde openingstijden zou deze nu open moeten zijn.<br /><br />
              Misschien zijn de openingstijden fout ingesteld?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
              <button style={{ border: `1px solid ${colors.border}`, borderRadius: '999px', padding: '12px 24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', background: colors.background, color: colors.text }} onClick={() => setShowClosedWarning(false)}>Melding Negeren</button>
              <button style={{ border: 'none', borderRadius: '999px', padding: '12px 24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', background: '#ea7317', color: 'white' }} onClick={() => setShowClosedWarning(false)}>Instellingen Controle</button>
            </div>
          </div>
        </div>
      )}

      {/* Test Order Modal */}
      {showTestOrderModal && (
        <TestOrderModal>
          <TestOrderModalContent>
            <TestOrderModalTitle>Test Order Aanmaken</TestOrderModalTitle>
            <TestOrderModalDescription>Kies het type test order. Test orders worden aangemaakt met status 'bereiding'.</TestOrderModalDescription>
            <TestOrderOptions>
              <TestOrderOption selected={testOrderType === 'pickup'} onClick={() => setTestOrderType('pickup')}>
                <TestOrderOptionIcon><ShoppingBag size={20} /></TestOrderOptionIcon>
                <TestOrderOptionText><TestOrderOptionTitle>Afhalen</TestOrderOptionTitle></TestOrderOptionText>
              </TestOrderOption>
              <TestOrderOption selected={testOrderType === 'delivery'} onClick={() => setTestOrderType('delivery')}>
                <TestOrderOptionIcon><Truck size={20} /></TestOrderOptionIcon>
                <TestOrderOptionText><TestOrderOptionTitle>Bezorgen</TestOrderOptionTitle></TestOrderOptionText>
              </TestOrderOption>
            </TestOrderOptions>
            <TestOrderModalButtons>
              <TestOrderModalButton onClick={() => setShowTestOrderModal(false)}>Annuleren</TestOrderModalButton>
              <TestOrderModalButton primary onClick={confirmTestOrder}>Aanmaken</TestOrderModalButton>
            </TestOrderModalButtons>
          </TestOrderModalContent>
        </TestOrderModal>
      )}

      <StatusPopup
        show={showStatusPopup}
        lastUpdateTime={lastUpdateTime}
        shopStatus={currentShopStatus}
        activeOrderCount={tabCounts.new + tabCounts.preparing + tabCounts.ready + tabCounts.out_for_delivery}
        onClose={() => setShowStatusPopup(false)}
        onForceSync={async () => { try { await DataSyncService.forceSync(); } catch {} setShowStatusPopup(false); }}
        forceSyncing={false}
      />
      
    </Container>
  );
}